import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';

import panelModes, { PanelMode } from './panelModes';
import {
  DOCKED_PANEL_WIDTH, MIN_PANEL_WIDTH, clampDockedPanelWidth, dockedPanelWidthLimits,
  nextChosenDockedPanelWidth,
} from './dockedPanelWidth';
import useWidthBesideScrollbar from '../../hooks/useWidthBesideScrollbar';

export const PANEL_MODE_STORAGE_KEY = 'pingwin.panelMode';

// Storage can be blocked or full, and the panel then opens floating as it did before.
const readStoredMode = (): PanelMode => {
  try {
    const stored = localStorage.getItem(PANEL_MODE_STORAGE_KEY);
    return stored === panelModes.DOCKED ? panelModes.DOCKED : panelModes.FLOATING;
  } catch {
    return panelModes.FLOATING;
  }
};

const storeMode = (mode: PanelMode) => {
  try {
    localStorage.setItem(PANEL_MODE_STORAGE_KEY, mode);
  } catch {
    // nothing to do
  }
};

type PinguDock = {
  open: boolean;
  setOpen: (open: boolean) => void;
  // Kept while there is no room to dock, so the panel docks again once there is.
  mode: PanelMode;
  setMode: (mode: PanelMode) => void;
  canDock: boolean;
  docked: boolean;
  // The docked panel's width: the member's chosen width, narrowed to the room beside the page.
  dockedWidth: number;
  // The widest the docked panel can be right now.
  dockedMaxWidth: number;
  // Sets the chosen width, kept within the limits that apply right now. While the panel
  // is narrowed, asking for its widest keeps the wider chosen width.
  setDockedWidth: (width: number) => void;
};

const PinguDockContext = createContext<PinguDock>({
  open: false,
  setOpen: () => {},
  mode: panelModes.FLOATING,
  setMode: () => {},
  canDock: false,
  docked: false,
  dockedWidth: DOCKED_PANEL_WIDTH,
  dockedMaxWidth: DOCKED_PANEL_WIDTH,
  setDockedWidth: () => {},
});

// Wraps the page rather than living in the launcher, so the page narrows in the same
// render as the panel docks and floats, and the choice survives closing the panel.
// The choice is also kept in localStorage, so it survives a reload on this browser.
export const PinguDockContextProvider = ({
  sidebarWidth,
  children,
}: {
  sidebarWidth: number;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  // A remembered dock only applies while there is room for it, through canDock below.
  const [mode, setStateMode] = useState<PanelMode>(readStoredMode);
  const setMode = useCallback((nextMode: PanelMode) => {
    setStateMode(nextMode);
    storeMode(nextMode);
  }, []);
  const widthBesideScrollbar = useWidthBesideScrollbar();

  // Lives only for this tab. Kept apart from the width shown, so the panel goes back to
  // it when the room beside the page narrowed it and then comes back.
  const [chosenWidth, setChosenWidth] = useState(DOCKED_PANEL_WIDTH);

  const limits = dockedPanelWidthLimits(widthBesideScrollbar, sidebarWidth);
  const canDock = limits !== null;
  const dockedWidth = limits ? clampDockedPanelWidth(chosenWidth, limits) : DOCKED_PANEL_WIDTH;
  const dockedMaxWidth = limits?.max ?? MIN_PANEL_WIDTH;
  const docked = open && mode === panelModes.DOCKED && canDock;

  const minWidth = limits?.min;
  const maxWidth = limits?.max;
  const setDockedWidth = useCallback((width: number) => {
    if (minWidth === undefined || maxWidth === undefined) return;
    setChosenWidth((chosen) => nextChosenDockedPanelWidth(
      width,
      chosen,
      { min: minWidth, max: maxWidth },
    ));
  }, [minWidth, maxWidth]);

  const value = useMemo(() => ({
    open,
    setOpen,
    mode,
    setMode,
    canDock,
    docked,
    dockedWidth,
    dockedMaxWidth,
    setDockedWidth,
  }), [open, mode, setMode, canDock, docked, dockedWidth, dockedMaxWidth, setDockedWidth]);

  return (
    <PinguDockContext.Provider value={value}>
      {children}
    </PinguDockContext.Provider>
  );
};

export const useDockContext = () => useContext(PinguDockContext);
